import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useEditEmbryos } from '@/services/embryo-service';

import { type EmbryoFormValues, recaneEmbryosFormSchema } from './schema';
import { RecaneEmbryosFormProps } from './types';
import {
  extractBaseCaneCode,
  generateNextCaneCode,
} from './utils/cane-code-generator';

export const RecaneEmbryosForm = ({
  embryos,
  existingCaneCodes = [],
  onCancel,
  onSuccess,
  onError,
  onSubmitValues,
  submitLabel,
  initialValues,
}: RecaneEmbryosFormProps) => {
  const { t } = useTranslation();
  const isRecanedIndicatorEnabled = useFeatureFlag(
    'Shipping_Recaned_Indicator',
  );
  const [isFormValid, setIsFormValid] = useState(false);

  const { mutateAsync: editEmbryosMutation, isPending: isSubmitting } =
    useEditEmbryos();

  const methods = useForm<EmbryoFormValues>({
    resolver: zodResolver(recaneEmbryosFormSchema),
    defaultValues: {
      embryos: [],
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    trigger,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = methods;
  const formEmbryos = watch('embryos');

  useEffect(() => {
    if (embryos && embryos.length > 0 && formEmbryos.length === 0) {
      if (initialValues && initialValues.embryos.length > 0) {
        reset(initialValues);
        return;
      }
      const initialEmbryos = embryos.map((embryo) => ({
        embryoId: embryo.id,
        name: embryo.name || '',
        currentCane: embryo.cane || null,
        newCane: '',
      }));
      reset({ embryos: initialEmbryos });
    }
  }, [embryos, formEmbryos.length, reset, initialValues]);

  useEffect(() => {
    if (formEmbryos.length > 0) {
      const needsGeneration = formEmbryos.some((e) => !e.newCane);
      if (!needsGeneration) return;

      const embryosNeedingCodes = formEmbryos
        .map((embryo, index) => ({ embryo, index }))
        .filter(({ embryo }) => !embryo.newCane);

      if (embryosNeedingCodes.length === 0) return;

      const formCodes = formEmbryos
        .map((e) => e.newCane)
        .filter(Boolean) as string[];
      const existingCodes = [...new Set([...existingCaneCodes, ...formCodes])];

      const currentCaneGroups = new Map<
        string,
        Array<{ embryo: (typeof formEmbryos)[0]; index: number }>
      >();

      embryosNeedingCodes.forEach(({ embryo, index }) => {
        const currentCane = embryo.currentCane || '';
        if (!currentCaneGroups.has(currentCane)) {
          currentCaneGroups.set(currentCane, []);
        }
        currentCaneGroups.get(currentCane)!.push({ embryo, index });
      });

      const codesToSet: Array<{ index: number; code: string }> = [];

      currentCaneGroups.forEach((group, currentCane) => {
        const base = extractBaseCaneCode(currentCane);
        const allExistingCodesForBase = existingCodes.filter((code) =>
          code.startsWith(base),
        );

        const nextCode = generateNextCaneCode(currentCane, [
          ...allExistingCodesForBase,
          ...codesToSet.map((c) => c.code),
        ]);

        group.forEach(({ index }) => {
          codesToSet.push({ index, code: nextCode });
        });
      });

      codesToSet.forEach(({ index, code }) => {
        setValue(`embryos.${index}.newCane`, code, { shouldValidate: false });
      });
    }
  }, [formEmbryos, setValue, existingCaneCodes]);

  useEffect(() => {
    const validateForm = async () => {
      const isValid = await trigger('embryos');
      const allCaneCodes = formEmbryos
        .map((e) => e.newCane || '')
        .filter(Boolean);
      setIsFormValid(isValid && allCaneCodes.length === formEmbryos.length);
    };
    validateForm();
  }, [formEmbryos, trigger]);

  const onSubmit = async (data: EmbryoFormValues) => {
    try {
      if (onSubmitValues) {
        await onSubmitValues(data);
        return;
      }

      const editInputs = data.embryos.map((embryo) => ({
        embryoId: embryo.embryoId,
        cane: embryo.newCane,
        ...(isRecanedIndicatorEnabled ? { recaned: true } : {}),
      }));

      const results = await editEmbryosMutation(editInputs);

      const hasErrors = results.some((result) => !result.success);
      if (hasErrors) {
        const errorMessages = results
          .filter((result) => !result.success)
          .map((result) => result.message || t('common.unknownError'))
          .join(', ');
        throw new Error(errorMessages);
      }

      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      if (onError) {
        onError(error);
      }
    }
  };

  if (!embryos || embryos.length === 0) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Message
          severity="info"
          text={t('embryo.noEmbryosSelectedForRecaning')}
        />
        <Button label={t('common.cancel')} onClick={onCancel} />
      </div>
    );
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(onSubmit)(e);
  };

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  return (
    <form
      onSubmit={handleFormSubmit}
      onKeyDown={handleFormKeyDown}
      onClick={(e) => e.stopPropagation()}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold">
          {t('embryo.assignNewCaneCodes')}
        </h3>
        <div className="flex flex-col gap-4">
          {formEmbryos.map((embryo, index) => {
            const hasError = errors.embryos?.[index]?.newCane;

            return (
              <Card key={embryo.embryoId} className="p-2">
                <div className="mb-2 font-medium">{embryo.name}</div>
                {embryo.currentCane && (
                  <div className="text-color-secondary mb-2 text-sm">
                    {t('common.current')}: {embryo.currentCane}
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <Controller
                    name={`embryos.${index}.newCane`}
                    control={control}
                    render={({ field }) => (
                      <div className="flex-1">
                        <InputText
                          {...field}
                          placeholder={t('embryo.enterCaneCode')}
                          className={`w-full ${hasError ? 'p-invalid' : ''}`}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        {hasError && (
                          <small className="p-error">
                            {errors.embryos?.[index]?.newCane?.message}
                          </small>
                        )}
                      </div>
                    )}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button
          type="button"
          label={t('common.cancel')}
          severity="secondary"
          onClick={onCancel}
        />
        <Button
          type="submit"
          label={submitLabel ?? t('common.submit')}
          disabled={isSubmitting || !isFormValid}
          icon={
            isSubmitting ? (
              <ProgressSpinner style={{ width: '16px', height: '16px' }} />
            ) : undefined
          }
        />
      </div>
    </form>
  );
};
